import { useColorModeValue } from '@chakra-ui/react';
import JSConfetti from 'js-confetti';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Image, Progress, Text, TextProps } from '@bonusly/design-system';

import { Reveal } from '../../../../../../../components/display/reveal';
import { useHomepageRedesignEnabled } from '../../BonusFeed/hooks/useHomepageRedesignEnabled';
import { useDaysRemainingInMonth } from '../hooks/use-days-remaining-in-month';
import { useDisplayConfetti } from '../hooks/use-display-confetti';
import { useFeedbackProgress, useRecognitionProgress } from '../hooks/use-progress';
import { Person } from '../types';
import { FeedbackProgress } from './feedback-progress';

interface ProgressBarProps {
	people: Person[];
}

export const ProgressBar: FC<ProgressBarProps> = ({ people }) => {
	const { t } = useTranslation('tracker_widget');
	const daysRemaining = useDaysRemainingInMonth();
	const progressBg = useColorModeValue('white', 'blueAlpha.300');

	const { feedbackProgress } = useFeedbackProgress(people);

	const { directReportsRecognized, directReportsTotal, recognitionFace, recognitionSvgPos, recognitionProgress, recognitionVisualProgress } =
		useRecognitionProgress(people);

	const showConfetti = () => {
		const canvas = document.getElementById('progressBarWrapper') as HTMLCanvasElement;
		const jsConfetti = new JSConfetti({ canvas });
		jsConfetti.addConfetti({
			confettiColors: ['#1E9F54', '#1670DF', '#8C75EB', '#F04747', '#FCC84F'],
		});
	};

	const { setRecognitionLocalStorageData, setFeedbackLocalStorageData, shouldDisplayFeedbackConfetti, shouldDisplayRecognitionConfetti } = useDisplayConfetti({
		feedbackProgress,
		recognitionProgress,
	});

	const homepageRedesignEnabled = useHomepageRedesignEnabled();

	useEffect(() => {
		if (shouldDisplayRecognitionConfetti || shouldDisplayFeedbackConfetti) {
			showConfetti();
			if (shouldDisplayRecognitionConfetti) setRecognitionLocalStorageData({ dateShown: new Date() });
			if (shouldDisplayFeedbackConfetti) setFeedbackLocalStorageData({ dateShown: new Date() });
		}
	}, [shouldDisplayFeedbackConfetti, shouldDisplayRecognitionConfetti]);

	return (
		<Box padding={3} borderRadius="md" background="blueAlpha.200" position="relative">
			<Flex justifyContent="space-between" alignItems="end" mb={1}>
				<StyledProgressLabel>{t('progress_bar.recognized_title')}</StyledProgressLabel>
				<StyledProgressLabel>
					{directReportsRecognized}/{directReportsTotal}
				</StyledProgressLabel>
			</Flex>
			<Progress
				aria-label={t('progress_bar.recognition_progress_label', {
					recognized: directReportsRecognized,
					total: directReportsTotal,
				})}
				value={recognitionVisualProgress}
				height={6}
				borderRadius={100}
				backgroundColor={progressBg}
				styleConfig={{
					baseStyle: {
						filledTrack: {
							backgroundColor: 'blue.200',
						},
					},
				}}
			>
				<Image src={recognitionFace} style={recognitionSvgPos} alt="" />
			</Progress>

			<Reveal if={!homepageRedesignEnabled}>
				<FeedbackProgress people={people} />
			</Reveal>

			<Reveal if={!homepageRedesignEnabled}>
				<Text align="right" color="text.softer" fontWeight="medium" fontSize={1} mt={2}>
					{t('progress_bar.days_remaining', {
						count: daysRemaining,
					})}
				</Text>
				<canvas
					style={{
						position: 'absolute',
						width: '100%',
						height: '100%',
						top: 0,
						left: 0,
					}}
					id="progressBarWrapper"
					height=""
					width=""
				></canvas>
			</Reveal>
		</Box>
	);
};

export const StyledProgressLabel = (props: TextProps) => {
	return <Text fontSize={1} fontWeight="semibold" lineHeight={5} {...props} />;
};
